
// services/api.ts
const getApiBaseUrl = () => {
    return 'https://backend-sable-five-58.vercel.app/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = (overrideBusinessId?: string) => {
  const token = localStorage.getItem('authToken');
  const businessId = overrideBusinessId || localStorage.getItem('currentBusinessId');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (businessId) headers['x-business-id'] = businessId;
  return headers;
};

const getCurrentBusinessId = (): string | null => localStorage.getItem('currentBusinessId');

const db = {
  get: (key: string) => { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : []; } catch { return []; } },
  set: (key: string, data: any) => { localStorage.setItem(key, JSON.stringify(data)); },
};

class ApiService {
  getBaseUrl(): string { return API_BASE_URL; }
  // ============ AUTH API ============
  async login(credentials: { username: string; password: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier: credentials.username, password: credentials.password }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || (error.errors?.[0]?.msg) || 'Đăng nhập thất bại.');
    }
    return await response.json();
  }

  async register(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const ct = response.headers.get('content-type');
    if (ct && ct.includes('application/json')) {
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Register failed');
      return result;
    }
    throw new Error(`Server Error (${response.status})`);
  }

  async socialLogin(token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/social-login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }),
    });
    if (!response.ok) throw new Error('Social Auth failed');
    return await response.json();
  }

  async getProfile(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch profile');
    return await response.json();
  }

  async updateProfile(data: { name?: string; username?: string; phone?: string; dob?: string; gender?: string; address?: string; avatar?: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update profile');
    }
    return await response.json();
  }

  async logout(): Promise<void> { /* client-side clear handled by AuthContext */ }

  async getCurrentUser(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
      if (!response.ok) return null;
      return await response.json();
    } catch { return null; }
  }

  // ============ BUSINESS API ============
  async getMyBusinesses(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch businesses');
    return await response.json();
  }

  // Alias used by SalonContext
  async getBusinesses(): Promise<any> { return this.getMyBusinesses(); }

  async createBusiness(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to create business');
    return r;
  }

  async updateBusiness(id: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to update business');
    return r;
  }

  async deleteBusiness(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete business');
    return await response.json();
  }

  async joinBusiness(businessId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/join`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ businessId: businessId.trim() }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || data?.error || `Failed to join business (${response.status})`);
    return data;
  }

  // Alias for backward compat
  async joinBusinessByCode(code: string): Promise<any> { return this.joinBusiness(code); }

  // Get all members (users) of a business with their user info
  async getBusinessMembers(businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    if (!id) return { success: false, data: [] };
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members`, { headers: getAuthHeaders() });
    return await response.json().catch(() => ({ success: false, data: [] }));
  }

  // Owner/Manager: update a member's role
  async updateMemberRole(userId: string, role: string, businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members/${userId}/role`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ role }),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to update role');
    return r;
  }

  // Owner: remove a member
  async removeMember(userId: string, businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members/${userId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to remove member');
    return r;
  }

  // Owner: add a member directly
  async addMember(userId: string, role: string, businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ userId, role }),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to add member');
    return r;
  }

  // ============ DASHBOARD API ============
  async getDashboardStats(params?: { date?: string }): Promise<any> {
    try {
      let url = `${API_BASE_URL}/dashboard`;
      if (params?.date) {
        url += `?date=${encodeURIComponent(params.date)}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed');
      return await response.json();
    } catch (e) {
      return { data: { overview: {}, employeeStats: {}, bookingStats: {} } };
    }
  }

  // Health check
  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, { headers: getAuthHeaders() });
      return response.ok;
    } catch { return false; }
  }

  // ============ EMPLOYEE API ============
  async getEmployees(params?: any): Promise<any> {
    try {
      const query = params ? new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/employees${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch employees');
      return await response.json();
    } catch { return { success: false, data: [] }; }
  }

  async getEmployeeById(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch employee');
    return await response.json();
  }

  async createEmployee(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to create employee');
    return r;
  }

  async updateEmployee(id: number | string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to update employee');
    return r;
  }

  async deleteEmployee(id: number | string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to delete employee');
    return r;
  }

  async getEmployeeStats(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/employees/stats`, { headers: getAuthHeaders() });
      if (response.ok) return await response.json();
    } catch { }
    return { data: null };
  }

  async changeEmployeeStatus(id: number | string, status: string): Promise<any> {
    return this.updateEmployee(id, { status });
  }

  async searchEmployees(query: string): Promise<any> {
    return this.getEmployees({ search: query });
  }

  // ============ BOOKING API ============
  async getBookings(filters?: any): Promise<any> {
    const query = filters ? new URLSearchParams(filters).toString() : '';
    const response = await fetch(`${API_BASE_URL}/bookings${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch bookings');
    return await response.json();
  }

  async createBooking(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/bookings`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.message || result?.error || `Server error ${response.status}`);
    return result;
  }

  async updateBooking(id: number | string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update booking');
    return await response.json();
  }

  async deleteBooking(id: number | string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete booking');
    return await response.json();
  }

  async getTodayBookings(): Promise<any> {
    try {
      const today = new Date().toLocaleDateString('en-CA');
      const response = await fetch(`${API_BASE_URL}/bookings?date=${today}`, { headers: getAuthHeaders() });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch { return { data: [] }; }
  }

  // ============ SERVICE API ============
  async getServices(businessId?: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/services`, { headers: getAuthHeaders(businessId) });
      if (!response.ok) throw new Error('Failed');
      return await response.json();
    } catch { return { data: [] }; }
  }

  async createService(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/services`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to create service');
    return r;
  }

  async updateService(id: string | number, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update service');
    return await response.json();
  }

  async deleteService(id: string | number): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete service');
    return await response.json();
  }

  // ============ NOTIFICATION API ============
  async registerFCMToken(token: string, device: string, platform: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/token`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ token, device, platform }),
    });
    if (!response.ok) return null;
    return await response.json();
  }

  async unregisterFCMToken(token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/token`, {
      method: 'DELETE', headers: getAuthHeaders(), body: JSON.stringify({ token }),
    });
    if (!response.ok) return null;
    return await response.json();
  }

  async getNotifications(limit: number = 50, unreadOnly: boolean = false): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/notifications?limit=${limit}&unreadOnly=${unreadOnly}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch notifications');
      return await response.json();
    } catch { return { success: false, data: [] }; }
  }

  async markNotificationAsRead(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'PUT', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to mark as read');
    return await response.json();
  }

  async markAllNotificationsAsRead(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'PUT', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to mark all as read');
    return await response.json();
  }

  async getNotificationPreferences(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/notifications/preferences`, { headers: getAuthHeaders() });
      if (!response.ok) return null;
      return await response.json();
    } catch { return null; }
  }

  async updateNotificationPreferences(prefs: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(prefs),
    });
    if (!response.ok) throw new Error('Failed to update preferences');
    return await response.json();
  }

  // ============ SCHEDULE API ============
  async generateBookingCode(businessId: string): Promise<any> {
    // Trigger pre-save hook by doing a no-op update which generates the code
    const response = await fetch(`${API_BASE_URL}/public/migrate-codes`, {
      method: 'POST',
      headers: getAuthHeaders(businessId),
    });
    return await response.json().catch(() => ({}));
  }

  async getSchedules(params?: any): Promise<any> {
    const query = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/schedules${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch schedules');
    return await response.json();
  }

  async createOrUpdateSchedule(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/schedules`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to save schedule');
    return await response.json();
  }

  async deleteSchedule(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/schedules/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete schedule');
    return await response.json();
  }

  // ============ CUSTOMER API ============
  async getCustomers(params?: any): Promise<any> {
    try {
      const query = params ? new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/customers${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
      if (response.ok) return await response.json();
    } catch { }
    return { success: false, data: [] };
  }

  async getCustomerById(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch customer');
    return await response.json();
  }

  async createCustomer(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create customer');
    return await response.json();
  }

  async updateCustomer(id: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update customer');
    return await response.json();
  }

  async deleteCustomer(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete customer');
    return await response.json();
  }

  // ============ ONLINE BOOKING SETTINGS ============
  async getOnlineBookingSettings(): Promise<any> {
    try {
      const businessId = getCurrentBusinessId();
      if (!businessId) return { data: null };
      const response = await fetch(`${API_BASE_URL}/businesses/${businessId}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch business');
      const result = await response.json();
      const business = result.data || result;
      const bookingPage = business?.settings?.bookingPage;
      if (bookingPage) {
        return { data: bookingPage };
      }
      // Return default settings if none exist
      return {
        data: {
          title: business?.name || 'Online Booking',
          description: 'Treat yourself to a premium experience. Book your appointment in seconds.',
          fields: {
            customerName: { label: 'Full Name', placeholder: 'e.g. John Doe' },
            phone: { label: 'Phone Number', placeholder: '+84...' },
            email: { label: 'Email Address', placeholder: 'john@example.com' },
            service: { label: 'Select Services', placeholder: 'Choose a service' },
            date: { label: 'Preferred Date', placeholder: '' },
            time: { label: 'Preferred Time', placeholder: '' },
            staff: { label: 'Preferred Professional (Optional)', placeholder: 'Any available professional' },
            referrer: { label: 'Referrer (Optional)', placeholder: 'e.g. Friend, Facebook...', visible: false },
            notes: { label: 'Special Requests', placeholder: 'Tell us about any specific needs...' }
          },
          footerText: ''
        }
      };
    } catch (e) {
      console.error('Failed to load booking settings:', e);
      return { data: null };
    }
  }

  async updateOnlineBookingSettings(settings: any): Promise<any> {
    const businessId = getCurrentBusinessId();
    if (!businessId) throw new Error('No business selected');
    // First get current business to preserve other settings
    const bizResponse = await fetch(`${API_BASE_URL}/businesses/${businessId}`, { headers: getAuthHeaders() });
    if (!bizResponse.ok) throw new Error('Failed to fetch business');
    const bizResult = await bizResponse.json();
    const currentBusiness = bizResult.data || bizResult;
    const updatedSettings = {
      ...currentBusiness.settings,
      bookingPage: settings
    };
    const response = await fetch(`${API_BASE_URL}/businesses/${businessId}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ settings: updatedSettings }),
    });
    if (!response.ok) throw new Error('Failed to update booking settings');
    return await response.json();
  }

  // ============ PUBLIC BOOKING API (No Auth) ============
  async getPublicBookingPage(bookingCode: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/public/book/${bookingCode}`, { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to load booking page');
    }
    return await response.json();
  }

  async createPublicBooking(bookingCode: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/public/book/${bookingCode}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to create booking');
    }
    return await response.json();
  }

  // ============ UPLOAD API (Cloudinary) ============
  async uploadAvatar(file: File): Promise<{ url: string; publicId: string }> {
    const token = localStorage.getItem('authToken');
    const businessId = localStorage.getItem('currentBusinessId');
    const formData = new FormData();
    formData.append('image', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (businessId) headers['x-business-id'] = businessId;
    const response = await fetch(`${API_BASE_URL}/upload/avatar`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Avatar upload failed');
    }
    return await response.json();
  }

  async uploadBusinessLogo(file: File): Promise<{ url: string; publicId: string }> {
    const token = localStorage.getItem('authToken');
    const businessId = localStorage.getItem('currentBusinessId');
    const formData = new FormData();
    formData.append('image', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (businessId) headers['x-business-id'] = businessId;
    const response = await fetch(`${API_BASE_URL}/upload/logo`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Logo upload failed');
    }
    return await response.json();
  }

  async uploadBookingImages(files: FileList | File[]): Promise<{ success: boolean; imageUrls: string[] }> {
    const formData = new FormData();
    // 'images' matches the multer field name
    Array.from(files).forEach(file => formData.append('images', file));
    
    const response = await fetch(`${API_BASE_URL}/upload/booking-images`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Booking images upload failed');
    }
    return await response.json();
  }

  // ============ REVIEWS API ============
  async getReviews(params?: { date?: string; rating?: string; staffId?: string }): Promise<any> {
    const businessId = getCurrentBusinessId();
    const query = params ? new URLSearchParams(params as any).toString() : '';
    const response = await fetch(
      `${API_BASE_URL}/reviews${query ? '?' + query : ''}`,
      { headers: getAuthHeaders(businessId || '') }
    );
    if (!response.ok) throw new Error('Failed to fetch reviews');
    return await response.json();
  }

  // ============ EARNINGS API ============
  async getEarningsStats(businessId: string, startDate?: string, endDate?: string): Promise<any> {
    let url = `${API_BASE_URL}/earnings/${businessId}`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch earnings');
    return await response.json();
  }
}

export const apiService = new ApiService();

